import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, Copy, Check, Sparkles, AlertTriangle } from "lucide-react";
import { generateScripts } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

export default function ScriptGeneratorPage() {
  const { planMeta, aiCallsThisSession, incrementAiCalls } = useAuth();
  const [selectedAd, setSelectedAd] = useState("");
  const [customCopy, setCustomCopy] = useState("");
  const [platform, setPlatform] = useState("meta");
  const [tone, setTone] = useState("ugc");
  const [audience, setAudience] = useState("");
  const [product, setProduct] = useState("");
  const [generated, setGenerated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const remainingAiCalls = Math.max(0, planMeta.maxAiCallsPerDay - aiCallsThisSession);

  const sourceCopy = customCopy.trim();

  const handleGenerate = async () => {
    if (!sourceCopy || remainingAiCalls <= 0) return;
    setLoading(true);
    setError("");
    try {
      const data = await generateScripts({
        copy: sourceCopy,
        platform,
        tone,
        audience,
        product,
      });
      setResult(data);
      setGenerated(true);
      incrementAiCalls();
    } catch (err) {
      setError(err.message || "Unable to generate scripts right now.");
      setResult(null);
      setGenerated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const CopyBtn = ({ text, id }) => (
    <button
      onClick={() => handleCopy(text, id)}
      className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary transition-colors px-2 py-1 rounded-md hover:bg-primary/10"
      data-testid={`copy-btn-${id}`}
    >
      {copiedId === id ? <><Check className="w-3 h-3 text-emerald-500" />Copied</> : <><Copy className="w-3 h-3" />Copy</>}
    </button>
  );

  return (
    <div className="fade-in" data-testid="script-generator-page">
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight text-gray-900 mb-1">Script Generator</h1>
        <p className="text-sm text-gray-500">Generate improved ad scripts from competitor ads or your own input.</p>
      </div>

      {/* Input */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-testid="script-input-section">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Source Material</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Select tracked ad</label>
            <Select value={selectedAd} onValueChange={setSelectedAd} disabled>
              <SelectTrigger className="w-full h-9 text-sm bg-gray-50 border-gray-200 text-gray-400" data-testid="script-ad-select">
                <SelectValue placeholder="Coming soon" />
              </SelectTrigger>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Or paste ad copy</label>
            <input
              type="text"
              value={customCopy}
              onChange={(e) => { setCustomCopy(e.target.value); setSelectedAd(""); setGenerated(false); setError(""); }}
              placeholder="Paste ad copy here..."
              className="bg-white border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary/40 focus:ring-2 focus:ring-primary/15 focus:outline-none rounded-lg h-9 px-3 w-full transition-all"
              data-testid="script-custom-input"
            />
          </div>
        </div>

        <h2 className="text-sm font-semibold text-gray-900 mb-4 mt-6">Configuration</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Platform</label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="w-full h-9 text-xs bg-white border-gray-200 text-gray-700" data-testid="script-platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="meta" className="text-xs">Meta</SelectItem>
                <SelectItem value="tiktok" className="text-xs">TikTok</SelectItem>
                <SelectItem value="google" className="text-xs">Google</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Tone</label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="w-full h-9 text-xs bg-white border-gray-200 text-gray-700" data-testid="script-tone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="ugc" className="text-xs">UGC</SelectItem>
                <SelectItem value="brand" className="text-xs">Brand</SelectItem>
                <SelectItem value="aggressive" className="text-xs">Aggressive</SelectItem>
                <SelectItem value="informative" className="text-xs">Informative</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Target Audience</label>
            <input
              type="text" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="e.g., Men 25-34"
              className="bg-white border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary/40 focus:ring-2 focus:ring-primary/15 focus:outline-none rounded-lg h-9 px-3 w-full transition-all"
              data-testid="script-audience"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Product</label>
            <input
              type="text" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g., Gym apparel"
              className="bg-white border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary/40 focus:ring-2 focus:ring-primary/15 focus:outline-none rounded-lg h-9 px-3 w-full transition-all"
              data-testid="script-product"
            />
          </div>
        </div>

        <Button
          onClick={handleGenerate}
          disabled={!sourceCopy || remainingAiCalls <= 0}
          className="bg-primary text-primary-foreground hover:bg-radar-secondary h-9 px-5 text-sm font-medium shadow-sm disabled:opacity-40"
          data-testid="generate-btn"
        >
          {loading ? (
            <span className="flex items-center gap-2"><span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Generating...</span>
          ) : (
            <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" />Generate Script</span>
          )}
        </Button>
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        {remainingAiCalls <= 0 && (
          <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
            <span>AI limit reached for your current plan today. Upgrade your plan to run more AI generations.</span>
          </div>
        )}
      </div>

      {/* Results */}
      {generated && result && (
        <div className="space-y-4 fade-in" data-testid="script-results">
          {/* Full Scripts */}
          <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="full-scripts-section">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Full Scripts</h3>
            <div className="space-y-3">
              {(result.full_scripts || []).map((script, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm text-gray-700 leading-relaxed flex-1 whitespace-pre-wrap">{script}</p>
                    <CopyBtn text={script} id={`full-${i}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hooks */}
          <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="hooks-section">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Hook Variations</h3>
            <div className="space-y-2">
              {(result.hooks || []).map((hook, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                  <p className="text-sm text-gray-700">{hook}</p>
                  <CopyBtn text={hook} id={`hook-${i}`} />
                </div>
              ))}
            </div>
          </div>

          {/* UGC Script */}
          {result.ugc_script && (
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="ugc-script-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">UGC-Style Script (20-30s)</h3>
                <CopyBtn text={result.ugc_script} id="ugc" />
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{result.ugc_script}</p>
              </div>
            </div>
          )}

          {/* Headlines */}
          <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="headlines-section">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Headline Variations</h3>
            <div className="space-y-2">
              {(result.headlines || []).map((h, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{h}</p>
                  <CopyBtn text={h} id={`headline-${i}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!generated && (
        <div className="text-center py-16 border border-gray-200 rounded-lg bg-gray-50/50" data-testid="script-empty-state">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <FileText className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm text-gray-600 mb-1">Select an ad or paste copy to get started</p>
          <p className="text-xs text-gray-400">We'll generate improved scripts, hooks, and headlines.</p>
        </div>
      )}
    </div>
  );
}
